import { useMutation, useQuery } from 'react-query';
import type { AxiosResponse } from 'axios';
import type { User } from '@src/types/user';
import useUserSession from '@src/hooks/useUserSession';
import toast from 'react-hot-toast';
import { useSessionStore } from '@src/store';
import { BACKEND_API } from './axiosConfig';

type OAuthTokenResponse = {
    access_token: string;
    expires_in: number;
    id_token: string;
    refresh_token: string;
    scope: string;
    token_type: string;
};

const useRedeemAuthorizationCodeMutation = (mutationOpts = undefined) => {
    return useMutation(({ code }: { code: string }) => {
        const redirectUri = `${window.location.origin}/login-callback`;
        return BACKEND_API.post<any, AxiosResponse<OAuthTokenResponse>>(
            'auth/redeem_authorization_code',
            {
                redirect_uri: redirectUri,
                code,
            },
            {
                skipAuth: true,
            },
        );
    }, mutationOpts);
};

export const authLock = {
    isShowingRedirectToast: false,
};
export const useGetCurrentUserQuery = (queryOpts = {}) => {
    return useQuery(['getCurrentUser', 'me'], async () => {
        const response = (await BACKEND_API.get<any, AxiosResponse<User>>(
            '/me',
        )).data;
        const expectedDomain = response?.domain;
        if (
            expectedDomain // When domain is configured
            && typeof window !== 'undefined' // skip for SSR
            && window.location.hostname !== expectedDomain // When domain is different
            && window.location.hostname !== 'localhost' // Allow testing locally without issues
            && !window.location.hostname.match(/\.pages\.dev$/) // Allow testing on PRs without issues
            && !window.location.hostname.match(/\.railway\.app$/) // Allow testing on PRs without issues
            && !authLock.isShowingRedirectToast // Once
        ) {
            authLock.isShowingRedirectToast = true;
            toast.error(`Incorrect url. Redirecting to ${expectedDomain}...`, { duration: 10000 });
            // Logout first to prevent infinite loops
            await useSessionStore.getState().logout({ redirect: false });
            await new Promise((resolve) => { setTimeout(resolve, 5000); });
            window.location.href = `https://${expectedDomain}/logout?email=${encodeURIComponent(response.user_email)}`;
        }
        if (authLock.isShowingRedirectToast) {
            // Lock config queries from finishing
            await new Promise((_) => {});
        }
        return response;
    }, {
        refetchOnMount: false, // Do not refetch unless explicitly invalidated
        ...queryOpts,
    });
};

export const useGetAssumedUserQuery = ({ userId }: { userId: number }, queryOpts = {}) => {
    return useQuery(['getCurrentUser', userId], async () => {
        return (await BACKEND_API.get<any, AxiosResponse<User>>(
            `/users/${userId}`,
        )).data;
    }, {
        refetchOnMount: false, // Do not refetch unless explicitly invalidated
        ...queryOpts,
    });
};

export const useAlloyStatus = (queryOpts = undefined) => {
    const { user } = useUserSession();
    return useQuery(['getAlloyStatus', user.id], async () => {
        return (await BACKEND_API.get<any, AxiosResponse<User>>(
            `/alloy/update-status?uid=${user.id}`,
        )).data;
    }, queryOpts);
};

export default useRedeemAuthorizationCodeMutation;
